/** Hop dong cua cac endpoint /admin/* — dashboard va SDK dung chung type nay. */
import { z } from 'zod';
import { API_KEY_SCOPES, PROFILE_MODES } from './domain.js';
// ---------------------------------------------------------------------------
// API key
// ---------------------------------------------------------------------------
export const CreateApiKeyRequestSchema = z
    .object({
    label: z.string().min(1).max(120),
    scopes: z.array(z.enum(API_KEY_SCOPES)).min(1).default(['solve']),
    rateLimitPerMin: z.number().int().min(1).max(100000).default(120),
    /** null = khong gioi han. */
    dailyQuota: z.number().int().min(1).max(100000000).nullable().default(null),
})
    .catchall(z.unknown());
export const UpdateApiKeyRequestSchema = z
    .object({
    label: z.string().min(1).max(120).optional(),
    scopes: z.array(z.enum(API_KEY_SCOPES)).min(1).optional(),
    rateLimitPerMin: z.number().int().min(1).max(100000).optional(),
    dailyQuota: z.number().int().min(1).max(100000000).nullable().optional(),
    enabled: z.boolean().optional(),
})
    .catchall(z.unknown());
// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------
/**
 * Kieu cua tung truong, KHONG kem gia tri mac dinh.
 *
 * ============================================================================
 *  VI SAO PHAI TACH RA — day la mot loi da xay ra that va rat kho thay.
 *
 *  Truoc day `ProfileUpdateSchema` la `ProfileInputSchema.partial()`. Nghe hop ly,
 *  nhung `.partial()` cua Zod chi lam truong thanh TUY CHON — no KHONG go bo
 *  `.default()`. Khi mot truong vang mat, `ZodOptional` van chuyen xuong `ZodDefault`
 *  ben trong va lay gia tri mac dinh ra.
 *
 *  Hau qua: `PATCH { "minConfidence": 0.01 }` — mot request doi DUNG MOT truong —
 *  lai ghi de ca sau truong khac ve mac dinh, trong do co `mode: 'off'`. Tuc la
 *  chinh sua mot con so lam TAT HAN profile, va khong co thong bao nao ca. Trieu
 *  chung o phia nguoi dung la moi captcha bong dung tra `no_solver`.
 *
 *  Da mat mot buoi de lan ra, vi moi tang deu "dung": schema parse thanh cong, DB
 *  ghi thanh cong, API tra 200 kem profile moi. Chi co dieu no khong phai thu nguoi
 *  dung yeu cau.
 *
 *  Nen: schema TAO ap mac dinh (POST can gia tri day du), schema CAP NHAT thi khong.
 * ============================================================================
 */
const PROFILE_FIELDS = {
    key: z
        .string()
        .min(1)
        .max(80)
        .regex(/^[a-z0-9][a-z0-9._-]*$/, 'chi cho phep chu thuong, so, dau . _ -'),
    label: z.string().min(1).max(160),
    taskType: z.string().min(1).max(80),
    solver: z.string().min(1).max(40),
    matchHosts: z.array(z.string().min(1)),
    matchHints: z.record(z.string(), z.unknown()),
    mode: z.enum(PROFILE_MODES),
    canaryPercent: z.number().int().min(0).max(100),
    minConfidence: z.number().min(0).max(1),
    config: z.record(z.string(), z.unknown()),
    enabled: z.boolean(),
};
/** POST /admin/profiles — ap mac dinh cho nhung truong nguoi dung khong khai. */
export const ProfileInputSchema = z
    .object({
    ...PROFILE_FIELDS,
    matchHosts: PROFILE_FIELDS.matchHosts.default([]),
    matchHints: PROFILE_FIELDS.matchHints.default({}),
    mode: PROFILE_FIELDS.mode.default('off'),
    canaryPercent: PROFILE_FIELDS.canaryPercent.default(10),
    minConfidence: PROFILE_FIELDS.minConfidence.default(0.9),
    config: PROFILE_FIELDS.config.default({}),
    enabled: PROFILE_FIELDS.enabled.default(true),
})
    .catchall(z.unknown());
/**
 * PATCH /admin/profiles/:key — CHI nhung truong nguoi dung thuc su gui len.
 * Khong `.default()` o bat ky dau, xem chu thich tren PROFILE_FIELDS.
 */
export const ProfileUpdateSchema = z.object(PROFILE_FIELDS).partial().catchall(z.unknown());
// ---------------------------------------------------------------------------
// Upstream
// ---------------------------------------------------------------------------
/**
 * PATCH /admin/upstream — CHI nhung truong nguoi dung thuc su gui len.
 *
 * KHONG dat `.default()` o day, cung mot ly do voi PROFILE_FIELDS phia tren: mot
 * `PATCH { "timeoutMs": 5000 }` se lang le ghi `enabled: false` va TAT HAN duong lui
 * tra phi. Nguoi van hanh chi dinh doi mot con so timeout, va nhan lai mot he thong
 * khong con fallback — khong bao loi, khong canh bao.
 */
export const UpstreamSettingsSchema = z
    .object({
    enabled: z.boolean().optional(),
    provider: z.string().min(1).optional(),
    /** Ghi vao DB thi luu, doc ra thi LUON bi che (chi tra 4 ky tu cuoi). */
    apiKey: z.string().optional(),
    baseUrl: z.string().optional(),
    softId: z.number().int().min(0).optional(),
    timeoutMs: z.number().int().min(1000).max(600000).optional(),
    /** Danh sach profileKey duoc phep fallback. Rong = cho phep tat ca. */
    onlyProfiles: z.array(z.string()).optional(),
})
    .catchall(z.unknown());
// ---------------------------------------------------------------------------
// Thong ke / task / sample
// ---------------------------------------------------------------------------
export const STATS_RANGES = ['24h', '7d', '30d'];
export const StatsQuerySchema = z.object({
    range: z.enum(STATS_RANGES).default('7d'),
    apiKey: z.coerce.number().int().positive().optional(),
});
export const TaskListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(500).default(100),
    cursor: z.coerce.number().int().positive().optional(),
    status: z.string().optional(),
    profileKey: z.string().optional(),
    apiKey: z.coerce.number().int().positive().optional(),
});
export const SampleListQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
    /**
     * Phan trang theo CON TRO (id giam dan), khong phai theo `offset`.
     *
     * Lay `nextCursor` cua trang truoc dua vao day. Con tro on dinh hon offset khi co mau
     * moi chen vao lien tuc — voi offset thi mot mau moi lam moi trang sau do lech di mot
     * dong, va nguoi duyet ca kho se doc trung / bo sot ma khong biet.
     */
    cursor: z.coerce.number().int().positive().optional(),
    /**
     * KHONG duoc ho tro — khai o day de TU CHOI tuong minh thay vi bo qua im lang.
     *
     * `GET /admin/dictionary` co nhan `offset`, nen mot nguoi da dung endpoint do rat de
     * gui `offset` sang day. Zod mac dinh loai bo khoa la, tuc endpoint tra ve DUNG TRANG
     * DAU mai mai va nguoi duyet kho mau thay id lap lai ma khong hieu vi sao. Da xay ra
     * that: mot nguoi dung keo 40.000 dong roi moi phat hien ra.
     */
    offset: z
        .never({
        message: 'Duong nay phan trang bang `cursor` chu khong phai `offset` — lay `nextCursor` ' +
            'cua trang truoc dua vao `cursor`. (Rieng /admin/dictionary thi dung `offset`.)',
    })
        .optional(),
    type: z.string().optional(),
    profile: z.string().optional(),
    verified: z
        .union([z.literal('true'), z.literal('false')])
        .optional()
        .transform((v) => (v === undefined ? undefined : v === 'true')),
});
//# sourceMappingURL=admin.js.map